"use client"

import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, type ControllerRenderProps, type ControllerFieldState } from "react-hook-form"
import { ChevronRightIcon, type LucideIcon } from "lucide-react"
import { z } from "zod"

import { Button, Form, FormControlledField, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useApplyApiFieldErrors } from "@/shared/lib/api-errors/api-form-errors"

/**
 * Settings as rows (the M1 pattern of the desktop "Ma branche" and workspace overview): one soft card per topic,
 * each row an icon, a label, an optional hint and the current value. Clicking an editable row turns THAT row into
 * an editor in place, saved on its own; a read-only row has no chevron and does nothing.
 *
 * Only one row edits at a time, so the page never holds two half-finished drafts. The host owns which one with
 * {@link useInlineSettingsEditing}.
 */

/** Which row is open, by the host's own row id. Opening a row closes any other. */
export function useInlineSettingsEditing<TRowId extends string>() {
  const [activeRowId, setActiveRowId] = React.useState<TRowId | null>(null)
  const open = React.useCallback((rowId: TRowId) => setActiveRowId(rowId), [])
  const close = React.useCallback(() => setActiveRowId(null), [])
  return { activeRowId, open, close, isOpen: (rowId: TRowId) => activeRowId === rowId }
}

export function InlineSettingsGroup({
  title,
  children,
  className,
}: {
  title: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={cn("flex min-w-0 flex-col gap-2", className)}>
      <h2 className='pl-1 text-sm font-bold'>{title}</h2>
      <div className='bg-card divide-border/60 divide-y overflow-hidden rounded-2xl border'>{children}</div>
    </section>
  )
}

type InlineSettingsRowProps = {
  icon: LucideIcon
  label: React.ReactNode
  hint?: React.ReactNode
  /** What the setting is now. A muted placeholder is shown by the host when empty. */
  value: React.ReactNode
  /** Opens the editor. Absent: the row is read-only (no chevron, not clickable). */
  onEdit?: () => void
  /** The editor, rendered in place of the value while this row is the open one. */
  editor?: React.ReactNode
  editing?: boolean
}

export function InlineSettingsRow({ icon: Icon, label, hint, value, onEdit, editor, editing = false }: InlineSettingsRowProps) {
  const head = (
    <>
      <span className='bg-muted text-muted-foreground flex size-[34px] shrink-0 items-center justify-center rounded-full'>
        <Icon className='size-[15px]' aria-hidden />
      </span>
      <span className='flex min-w-0 flex-1 flex-col'>
        <span className='text-sm font-bold'>{label}</span>
        {hint ? <span className='text-muted-foreground text-[12.5px]'>{hint}</span> : null}
      </span>
    </>
  )

  if (editing && editor) {
    return (
      <div className='flex flex-col gap-3 px-5 py-3.5' data-editing='true'>
        <div className='flex items-center gap-3.5'>{head}</div>
        <div className='sm:pl-12'>{editor}</div>
      </div>
    )
  }

  const trailing = (
    <>
      <span className='text-foreground max-w-[55%] truncate text-right text-sm font-bold'>{value}</span>
      {onEdit ? (
        <ChevronRightIcon className='text-muted-foreground size-[15px] shrink-0' aria-hidden />
      ) : (
        <span className='w-[15px] shrink-0' aria-hidden />
      )}
    </>
  )

  if (!onEdit) {
    return <div className='flex items-center gap-3.5 px-5 py-3.5'>{head}{trailing}</div>
  }

  return (
    <button
      type='button'
      onClick={onEdit}
      className='hover:bg-muted/50 focus-visible:bg-muted/50 flex w-full items-center gap-3.5 px-5 py-3.5 text-left transition-colors outline-none'
    >
      {head}
      {trailing}
    </button>
  )
}

/** The inline editor's one field. `value` is the only key so a row's schema is just the field's own schema. */
type InlineFieldValues = { value: string }

export type InlineFieldRenderProps = {
  field: ControllerRenderProps<InlineFieldValues, "value">
  fieldState: ControllerFieldState
}

type InlineFieldEditorProps = {
  /** The field's Zod schema (validated with RHF before anything is sent). Every row edits text or a choice. */
  schema: z.ZodType<string>
  defaultValue: string
  renderInput: (props: InlineFieldRenderProps) => React.ReactNode
  /** Persists the one field. Throwing keeps the editor open; backend field errors land on the field. */
  onSave: (value: string) => Promise<void>
  onCancel: () => void
  /** The backend's name for this field, so its `fieldErrors` map onto the input. */
  apiFieldName?: string
  /** Feature error namespace for translating backend field errors. */
  errorDomain?: string
}

export function InlineFieldEditor({
  schema,
  defaultValue,
  renderInput,
  onSave,
  onCancel,
  apiFieldName,
  errorDomain,
}: InlineFieldEditorProps) {
  const t = useTranslations("common.inlineSettings")
  const formSchema = React.useMemo(() => z.object({ value: schema }), [schema])
  const form = useForm<InlineFieldValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { value: defaultValue },
  })
  const applyFieldErrors = useApplyApiFieldErrors<InlineFieldValues>(errorDomain)

  // The editor replaces the row the reader just clicked, so their focus moves into it, and Escape backs out.
  const formRef = React.useRef<HTMLFormElement | null>(null)
  React.useEffect(() => {
    const element = formRef.current
    if (!element) return
    element.querySelector<HTMLElement>("input, textarea, button[role='combobox'], [role='combobox']")?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      event.stopPropagation()
      onCancel()
    }
    element.addEventListener("keydown", onKeyDown)
    return () => element.removeEventListener("keydown", onKeyDown)
  }, [onCancel])

  const submit = form.handleSubmit(async (values) => {
    try {
      await onSave(values.value)
    } catch (error) {
      if (apiFieldName) applyFieldErrors(form.setError, error, { fieldNameMap: { [apiFieldName]: "value" } })
    }
  })

  return (
    <Form {...form}>
      <form ref={formRef} onSubmit={(event) => void submit(event)} className='flex flex-col gap-2.5'>
        <FormControlledField control={form.control} name='value' renderInput={(props) => renderInput(props)} />
        <div className='flex items-center justify-end gap-2'>
          <Button type='button' variant='outline' size='sm' className='rounded-full' onClick={onCancel}>
            {t("cancel")}
          </Button>
          <Button type='submit' size='sm' className='rounded-full' loading={form.formState.isSubmitting}>
            {t("save")}
          </Button>
        </div>
      </form>
    </Form>
  )
}

/** A muted "not set" for an empty value. */
export function InlineSettingsEmptyValue({ children }: { children: React.ReactNode }) {
  return <span className='text-muted-foreground font-semibold'>{children}</span>
}
